import { z } from "zod";
import { getDimensionDefinition } from "./dimension-registry";
import {
  DIMENSION_KEYS,
  FILTER_OPERATORS,
  METRIC_KEYS,
  NUMERIC_FIELD_KEYS,
  REPORT_DATA_SOURCES,
  REPORT_SCHEMA_VERSION,
  VISUALISATION_TYPES,
  type StudioReport,
} from "./report-definition";

const idSchema = z.string().trim().min(1).max(160);
const labelSchema = z.string().trim().min(1).max(200);
const descriptionSchema = z.string().trim().min(1).max(1_000);

function canonicalTimestamp(value: unknown): unknown {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? value : value.toISOString();
  }
  if (value && typeof value === "object" && "toDate" in value) {
    const toDate = (value as { toDate?: unknown }).toDate;
    if (typeof toDate === "function") {
      try {
        const date = (toDate as (this: unknown) => unknown).call(value);
        if (date instanceof Date && !Number.isNaN(date.getTime())) return date.toISOString();
      } catch {
        return value;
      }
    }
  }
  return value;
}

export const isoTimestampSchema = z.preprocess(
  canonicalTimestamp,
  z.iso.datetime({ offset: true }),
);

function isCanonicalDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day;
}

export const canonicalDateSchema = z.string().refine(isCanonicalDate, {
  message: "Expected a real calendar date in yyyy-MM-dd format.",
});

export const studioReportDateRangeSchema = z.object({
  startDate: canonicalDateSchema,
  endDate: canonicalDateSchema,
}).strict().refine((value) => value.startDate <= value.endDate, {
  message: "The report start date must be on or before the end date.",
  path: ["endDate"],
});

export const dimensionKeySchema = z.enum(DIMENSION_KEYS);
export const metricKeySchema = z.enum(METRIC_KEYS);
export const numericFieldKeySchema = z.enum(NUMERIC_FIELD_KEYS);
export const visualisationTypeSchema = z.enum(VISUALISATION_TYPES);
export const filterOperatorSchema = z.enum(FILTER_OPERATORS);

export const numberRangeValueSchema = z.object({
  minimum: z.number().finite(),
  maximum: z.number().finite(),
}).strict().refine((value) => value.minimum <= value.maximum, {
  message: "The minimum must be no greater than the maximum.",
  path: ["maximum"],
});

export const dateRangeValueSchema = studioReportDateRangeSchema;

export const relativeDateValueSchema = z.object({
  amount: z.number().int().positive().max(10_000),
  unit: z.enum(["days", "weeks", "months", "years"]),
}).strict();

export const reportFilterValueSchema = z.union([
  z.string().trim().min(1).max(300),
  z.array(z.string().trim().min(1).max(300)).min(1).max(100),
  z.number().finite(),
  z.boolean(),
  numberRangeValueSchema,
  dateRangeValueSchema,
  relativeDateValueSchema,
]);

const filterBaseShape = {
  id: idSchema,
  dimension: dimensionKeySchema,
  operator: filterOperatorSchema,
  value: reportFilterValueSchema.optional(),
};

function validateFilter(
  value: {
    dimension: z.infer<typeof dimensionKeySchema>;
    operator: z.infer<typeof filterOperatorSchema>;
    value?: z.infer<typeof reportFilterValueSchema> | undefined;
  },
  context: z.RefinementCtx,
): void {
  const noValueOperators = new Set(["is_known", "is_unknown", "yes", "no"]);
  const stringOperators = new Set(["is", "is_not", "before", "after", "calendar_year", "training_year"]);
  const numberOperators = new Set(["equals", "greater_than", "less_than"]);

  if (!getDimensionDefinition(value.dimension).filterOperations.includes(value.operator)) {
    context.addIssue({
      code: "custom",
      message: `${value.operator} is not available for ${value.dimension}.`,
      path: ["operator"],
    });
  }

  if (noValueOperators.has(value.operator)) {
    if (value.value !== undefined) {
      context.addIssue({ code: "custom", message: `${value.operator} does not take a value.`, path: ["value"] });
    }
    return;
  }
  if (value.value === undefined) {
    context.addIssue({ code: "custom", message: `${value.operator} requires a value.`, path: ["value"] });
    return;
  }
  if (stringOperators.has(value.operator) && typeof value.value !== "string") {
    context.addIssue({ code: "custom", message: `${value.operator} requires a text or date value.`, path: ["value"] });
  }
  if (numberOperators.has(value.operator) && typeof value.value !== "number") {
    context.addIssue({ code: "custom", message: `${value.operator} requires a number.`, path: ["value"] });
  }
  if (value.operator === "is_any_of" && !Array.isArray(value.value)) {
    context.addIssue({ code: "custom", message: "is_any_of requires one or more category values.", path: ["value"] });
  }
  if (value.operator === "between"
    && (typeof value.value !== "object" || Array.isArray(value.value)
      || (!("minimum" in value.value) && !("startDate" in value.value)))) {
    context.addIssue({ code: "custom", message: "between requires a numeric or date range.", path: ["value"] });
  }
  if (value.operator === "in_the_last"
    && (typeof value.value !== "object" || Array.isArray(value.value) || !("amount" in value.value))) {
    context.addIssue({ code: "custom", message: "in_the_last requires an amount and time unit.", path: ["value"] });
  }

  if (typeof value.value !== "string") return;
  const period = value.operator === "calendar_year"
    ? "calendar_year"
    : value.operator === "training_year"
      ? "training_year"
      : value.dimension;
  if (period === "calendar_year" && !/^\d{4}$/.test(value.value)) {
    context.addIssue({ code: "custom", message: "Calendar year must use yyyy format.", path: ["value"] });
  }
  if (period === "calendar_month" && !/^\d{4}-(?:0[1-9]|1[0-2])$/.test(value.value)) {
    context.addIssue({ code: "custom", message: "Calendar month must use yyyy-MM format.", path: ["value"] });
  }
  if (period === "training_year" && !/^\d{4}-08$/.test(value.value)) {
    context.addIssue({ code: "custom", message: "Training year must use its August start in yyyy-08 format.", path: ["value"] });
  }
}

export const globalReportFilterSchema = z.object({
  ...filterBaseShape,
  scope: z.literal("global"),
}).strict().superRefine(validateFilter);

export const widgetFilterSchema = z.object({
  ...filterBaseShape,
  scope: z.literal("widget"),
}).strict().superRefine(validateFilter);

export const calculationSortSchema = z.object({
  by: z.enum(["value", "label"]),
  direction: z.enum(["ascending", "descending"]),
}).strict();

export const calculationDefinitionSchema = z.object({
  metric: metricKeySchema,
  numericField: numericFieldKeySchema.optional(),
  primaryDimension: dimensionKeySchema.optional(),
  secondaryDimension: dimensionKeySchema.optional(),
  percentageDenominator: z.enum(["included_cases", "primary_group"]),
  sort: calculationSortSchema,
  categoryLimit: z.number().int().positive().max(1_000).optional(),
  includeEmptyCategories: z.boolean(),
}).strict().superRefine((value, context) => {
  const numericMetrics = new Set(["average", "minimum", "maximum", "sum"]);
  if (numericMetrics.has(value.metric) && value.numericField === undefined) {
    context.addIssue({
      code: "custom",
      message: `${value.metric} requires an eligible numeric field.`,
      path: ["numericField"],
    });
  }
  if (!numericMetrics.has(value.metric) && value.numericField !== undefined) {
    context.addIssue({
      code: "custom",
      message: `${value.metric} does not use a numeric field.`,
      path: ["numericField"],
    });
  }
  if (value.secondaryDimension !== undefined && value.primaryDimension === undefined) {
    context.addIssue({
      code: "custom",
      message: "A secondary grouping requires a primary grouping.",
      path: ["secondaryDimension"],
    });
  }
  if (value.secondaryDimension !== undefined && value.secondaryDimension === value.primaryDimension) {
    context.addIssue({
      code: "custom",
      message: "Primary and secondary groupings must be different.",
      path: ["secondaryDimension"],
    });
  }
  if (value.percentageDenominator === "primary_group" && value.primaryDimension === undefined) {
    context.addIssue({
      code: "custom",
      message: "A primary-group denominator requires a primary grouping.",
      path: ["percentageDenominator"],
    });
  }
});

export const visualisationDefinitionSchema = z.object({
  type: visualisationTypeSchema,
}).strict();

export const widgetDisplayOptionsSchema = z.object({
  showTitle: z.boolean(),
  showDescription: z.boolean(),
  showLegend: z.boolean(),
  showValues: z.boolean(),
  showCounts: z.boolean(),
  showPercentages: z.boolean(),
  showMissingValues: z.boolean(),
}).strict();

const widgetBaseShape = {
  id: idSchema,
  title: labelSchema,
  description: descriptionSchema.optional(),
  dataSource: z.enum(REPORT_DATA_SOURCES),
  filters: z.array(widgetFilterSchema).max(50),
  displayOptions: widgetDisplayOptionsSchema,
};

const analyticalReportWidgetSchema = z.object({
  ...widgetBaseShape,
  type: z.enum(["chart", "table", "metric"]),
  calculation: calculationDefinitionSchema,
  visualisation: visualisationDefinitionSchema,
}).strict().superRefine((value, context) => {
  const chartTypes = new Set([
    "vertical_bar",
    "horizontal_bar",
    "grouped_bar",
    "stacked_bar",
    "line",
    "donut",
  ]);
  if (value.type === "chart" && !chartTypes.has(value.visualisation.type)) {
    context.addIssue({ code: "custom", message: "A chart widget requires a chart visualisation.", path: ["visualisation", "type"] });
  }
  if (value.type === "table" && value.visualisation.type !== "table") {
    context.addIssue({ code: "custom", message: "A table widget requires the table visualisation.", path: ["visualisation", "type"] });
  }
  if (value.type === "metric" && value.visualisation.type !== "headline_metric") {
    context.addIssue({ code: "custom", message: "A metric widget requires the headline metric visualisation.", path: ["visualisation", "type"] });
  }
});

const textReportWidgetSchema = z.object({
  ...widgetBaseShape,
  type: z.literal("text"),
  calculation: z.null(),
  visualisation: z.object({ type: z.literal("text") }).strict(),
  textContent: z.string().max(20_000),
  unavailableReason: z.string().trim().min(1).max(1_000).optional(),
}).strict();

export const reportWidgetSchema = z.union([
  analyticalReportWidgetSchema,
  textReportWidgetSchema,
]);

export const reportLayoutItemSchema = z.object({
  widgetId: idSchema,
  x: z.number().int().min(0).max(11),
  y: z.number().int().min(0).max(100_000),
  width: z.number().int().min(1).max(12),
  height: z.number().int().min(1).max(100),
  minimumWidth: z.number().int().min(1).max(12).optional(),
  minimumHeight: z.number().int().min(1).max(100).optional(),
}).strict().superRefine((value, context) => {
  if (value.x + value.width > 12) {
    context.addIssue({ code: "custom", message: "The layout item must fit within the 12-column canvas.", path: ["width"] });
  }
  if (value.minimumWidth !== undefined && value.minimumWidth > value.width) {
    context.addIssue({ code: "custom", message: "Minimum width cannot exceed the saved width.", path: ["minimumWidth"] });
  }
  if (value.minimumHeight !== undefined && value.minimumHeight > value.height) {
    context.addIssue({ code: "custom", message: "Minimum height cannot exceed the saved height.", path: ["minimumHeight"] });
  }
});

export const studioReportSchema = z.object({
  id: idSchema,
  ownerId: idSchema,
  name: labelSchema,
  description: descriptionSchema.optional(),
  schemaVersion: z.literal(REPORT_SCHEMA_VERSION),
  dataSource: z.enum(REPORT_DATA_SOURCES),
  globalFilters: z.array(globalReportFilterSchema).max(50),
  dateRange: studioReportDateRangeSchema.optional(),
  widgets: z.array(reportWidgetSchema).max(100),
  layout: z.array(reportLayoutItemSchema).max(100),
  createdAt: isoTimestampSchema,
  updatedAt: isoTimestampSchema,
}).strict().superRefine((value, context) => {
  const widgetIds = new Set<string>();
  for (const [index, widget] of value.widgets.entries()) {
    if (widgetIds.has(widget.id)) {
      context.addIssue({ code: "custom", message: "Widget IDs must be unique within a report.", path: ["widgets", index, "id"] });
    }
    widgetIds.add(widget.id);
  }

  const layoutIds = new Set<string>();
  for (const [index, item] of value.layout.entries()) {
    if (!widgetIds.has(item.widgetId)) {
      context.addIssue({ code: "custom", message: "Layout refers to a widget that does not exist.", path: ["layout", index, "widgetId"] });
    }
    if (layoutIds.has(item.widgetId)) {
      context.addIssue({ code: "custom", message: "A widget may appear only once in the saved layout.", path: ["layout", index, "widgetId"] });
    }
    layoutIds.add(item.widgetId);
  }
  for (const [index, widget] of value.widgets.entries()) {
    if (!layoutIds.has(widget.id)) {
      context.addIssue({ code: "custom", message: "Every widget requires a saved layout item.", path: ["widgets", index, "id"] });
    }
  }

  if (new Date(value.updatedAt).getTime() < new Date(value.createdAt).getTime()) {
    context.addIssue({ code: "custom", message: "Updated time cannot be before created time.", path: ["updatedAt"] });
  }
});

export function parseCurrentStudioReport(value: unknown): StudioReport {
  return studioReportSchema.parse(value) as StudioReport;
}

export type StudioReportParseResult =
  | { readonly success: true; readonly data: StudioReport }
  | { readonly success: false; readonly error: z.ZodError };

export function safeParseCurrentStudioReport(value: unknown): StudioReportParseResult {
  const parsed = studioReportSchema.safeParse(value);
  return parsed.success
    ? { success: true, data: parsed.data as StudioReport }
    : { success: false, error: parsed.error };
}
